import uploadedDocs from "../../services/models/formModels/uploadedDocs.model";
import getOrderTestDisplayLabel from "../../utils/helpers/getOrderTestDisplayLabel";
import { normalizeSccClinicalSectionsFromApi } from "../../utils/helpers/sccClinicalSectionMapper";

const processBillingInfo = (billingInfo: any) => {
  return {
    ...billingInfo,
    additionalCodes: billingInfo?.additionalCodes?.join(", "),
  };
};

const formatApprovalDesktopData = (data: any) => {
  return data?.map((order: any) => {
    const additionalClinicians = order?.additionalClinician ?? [];

    const findDocByType = (type: string) =>
      order?.supportingDocuments.filter((doc: any) => doc?.type === type) ||
      null;

    const clinicians = additionalClinicians.map((clinician: any) => ({
      firstName: clinician.firstName || "",
      lastName: clinician.lastName || "",
      npi: clinician.npi || "",
      email: clinician.email || "",
      fax: clinician.fax || "",
    }));

    const supportingDocs: Partial<uploadedDocs> = {
      patientInfoDemographicSheet: findDocByType("demographics-sheet"),
      insuranceCard: findDocByType("insurance-card"),
      currentPrescriptionMedication: findDocByType(
        "current-prescription-medications"
      ),
      pathologyReport: findDocByType("patholog-report"),
      endoscopyReport: findDocByType("endoscopy-report"),
      clinicNotes: findDocByType("clinic-notes"),
      requisitionForm: findDocByType("requisition-form"),
      visitNotes: findDocByType("visit-notes"),
      lomn: findDocByType("lomn"),
      other: findDocByType("other"),
    };

    return {
      id: order?.id,
      orderStatus: order.orderStatus,
      patientInfo: {
        firstName: order?.patientInfo?.firstName,
        middleInitial: order?.patientInfo?.middleInitial,
        lastName: order?.patientInfo?.lastName,
        dateOfBirth: order?.patientInfo?.birthDate,
        sexAtBirth: order?.patientInfo?.gender,
        // patientName: order?.patientName,
      },
      testType: {
        test: order?.testTypes?.id,
        ...order?.testTypes,
      },
      header: {
        patientName: order?.patientFirstName,
        test: getOrderTestDisplayLabel(order, { useFullName: false }),
      },
      patientSecurity: {
        ssn: order?.patientInfo?.ssn,
        mrn: order?.patientInfo?.mrn,
      },
      patientContact: {
        address: order?.patientInfo?.address,
        city: order?.patientInfo?.city,
        state: order?.patientInfo?.state,
        zipcode: order?.patientInfo?.zipcode,
        phone: order?.patientInfo?.phone,
        email: order?.patientInfo?.email,
      },
      billingInfo: {
        icd10:
          order?.icdCodeDetails?.value +
          " " +
          order?.icdCodeDetails?.name,
        primaryInsurance: order?.billingInfo?.insuranceCompanyName,
        insuranceCompanyName: order?.billingInfo?.insuranceCompanyName,
        policyNumber: order?.billingInfo?.policyNumber,
        phone: order?.billingInfo?.phone,
        typeOfFacility: order?.billingInfo?.typeOfFacility,
        dateOfDischarge: order?.billingInfo?.dateOfDischarge,
      },
      additionalInfo: clinicians,
      billingInfoIDG: {
        primaryDiagnosedICD10:
          order?.icdCodeDetails?.value +
          " " +
          order?.icdCodeDetails?.name,
        additionalDiagnosedICD10: processBillingInfo(
          order?.billingInfo?.additionalCodes
        ),
        primaryInsurance: order?.billingInfo?.insuranceCompanyName,
        insuranceCompanyName: order?.billingInfo?.insuranceCompanyName,
        phone: order?.billingInfo?.phone,
        policyNumber: order?.billingInfo?.policyNumber,
      },
      clinicianInfoOrganization: {
        name: order?.organization?.name,
        address: order?.organization?.address,
        city: order?.organization?.city,
        state: order?.organization?.state,
        zipcode: order?.organization?.zipcode,
        phone: order?.organization?.phone,
        fax: order?.organization?.fax,
      },
      clinicianInfoPhysician: {
        firstName: order?.physician?.firstName,
        lastName: order?.physician?.lastName,
        suffix: order?.physician?.suffix,
        npi: order?.physician?.npi,
        specialty: order?.physician?.specialty,
        email: order?.physician?.email,
      },
      ...normalizeSccClinicalSectionsFromApi(order),
      clinicalInfoId3Melanoma: {
        isSentinelLymphNodeBiopsy: order?.isSentinelLymphNodeBiopsy,
        lymphNodeStatus: order?.lymphNodeStatus,
        isClinicallyPalpableNodes: order?.isClinicallyPalpableNodes,
        isIntrasitMetastasis: order?.isIntrasitMetastasis,
      },
      clinicialInfoId4UMUMSeq: {
        isExposedToRadiation: order?.isExposedToRadiation,
        isReportPrame: order?.isExposedToRadiation,
      },
      clinicalInfoId5DDM: {
        isPrimaryCutaneousMelanocyticNeoplasm:
          order?.isPrimaryCutaneousMelanocyticNeoplasm,
      },
      clinicalInfoId8IDG: {
        isAltheaDxNeuropsychotropicMedications:
          order?.isAltheaDxNeuropsychotropicMedications,
        isIDgNeuropsychotropicMedicationsCurrent:
          order?.isIDgNeuropsychotropicMedicationsCurrent,
        isIDgOTCHerbalMedsEnvironmentalFactorDietarySupplement:
          order?.isIDgOTCHerbalMedsEnvironmentalFactorDietarySupplement,
      },
      laboratoryInfo: {
        typeofSpecimen: order?.typeOfSpecimen,
        nameOfFacility: order?.labortaryInfo?.labortaryName,
        dateOfCollection: order?.labortaryInfo?.dateOfCollection,
        specimenId: order?.labortaryInfo?.specimenId,
        medicarePullDate: order?.medicarePullDate,
        phone: order?.labortaryInfo?.phone,
        fax: order?.labortaryInfo?.fax,
        pathologist: order?.labortaryInfo?.Pathologist,
        tumorSite: order?.labortaryInfo?.TumorSite,
        sampleLocationA: order?.sampleLocationA,
        sampleLocationB: order?.sampleLocationB,
      },
      laboratoryInfoIDG: {
        dateOfCollection: order?.labortaryInfo?.dateOfCollection,
        medicarePullDate: order?.medicarePullDate,
        isTelehealth: order?.isTeleHealth,
        facilityType: order?.facilityType,
      },
      supportingDocs: supportingDocs,
      guarantorName: order?.guarantorName,
      supportingSection: {
        id: order?.id,
        transitDocument: order?.transitDocument,
        signPermission: order?.isPhysicianSignPermission,
      },
      isTissueShipped: order?.isTissueShipped,
      shipTracking: order?.shipTracking,
      shipMethod: order?.shipMethod,
      isConsideredForTherapy: order?.isConsideredForTherapy,
    };
  });
};

export default formatApprovalDesktopData;
